import './style.css';
import { LoadingComponent, SVGCardRenderComponent } from '../../controls';
import { PackSettings } from '../../types/packSettings';
import { useMapsReducer } from './useMapsReducer';
import { Job, JobItem } from '../../types/job';
import useAppContext from '../../hooks/useAppContext';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataView } from 'primereact/dataview';
import { Chart } from 'primereact/chart';
import useMenu from '../../routes/routes';
import { Level } from '../../types/level';
import { useLocation } from 'react-router-dom';

const CHART_VISIBLE_LEVELS = 200;
const CHART_PIXEL_PER_LEVEL = 6;
const MIN_Y_PADDING = 5;

const MapsPage = () => {
  const {
    data,
    submitJob,
    recalculateDifficulty,
    recalculateLoading,
    reorderLevelsByDifficulty,
    reorderLoading,
  } = useMapsReducer();
  const { Lang } = useAppContext();
  const { goToLevelEdit, navigate } = useMenu();
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const { hash } = useLocation();
  const scrollExecuted = useRef(false);

  const onGenerate = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    numbers: number[]
  ) => {
    e.stopPropagation();
    const items = numbers.map((n) => {
      return { number: n } as JobItem;
    });
    const job = { lang: Lang, packId: data.PackId, items: [...items] } as Job;
    await submitJob(job);
  };

  const onEdit = (id: number) => goToLevelEdit(id);
  const onCreate = (number: number) => navigate(`/maps/new?number=${number}`);

  const onReorderClick = () => {
    confirmDialog({
      message:
        'Are you sure you want to redistribute level numbers within each pack settings range so difficulty increases by level?',
      header: 'Re-order levels',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        void reorderLevelsByDifficulty();
      },
    });
  };
  const chartPoints = useMemo(
    () => [...data.DifficultyPoints].sort((a, b) => a.number - b.number),
    [data.DifficultyPoints]
  );
  const chartData = useMemo(() => {
    if (chartPoints.length === 0) {
      return null;
    }

    return {
      labels: chartPoints.map((point) => point.number.toString()),
      datasets: [
        {
          label: 'Difficulty',
          data: chartPoints.map((point) => point.difficulty),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          tension: 0.35,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          pointHitRadius: 8,
        },
      ],
    };
  }, [chartPoints]);
  const chartStats = useMemo(() => {
    if (chartPoints.length === 0) {
      return null;
    }

    let minDifficulty = Number.POSITIVE_INFINITY;
    let maxDifficulty = Number.NEGATIVE_INFINITY;

    chartPoints.forEach((point) => {
      minDifficulty = Math.min(minDifficulty, point.difficulty);
      maxDifficulty = Math.max(maxDifficulty, point.difficulty);
    });

    return { minDifficulty, maxDifficulty };
  }, [chartPoints]);
  const chartRange = useMemo(() => {
    if (!chartStats) {
      return null;
    }

    const rawPadding = (chartStats.maxDifficulty - chartStats.minDifficulty) * 0.1;
    const padding = Math.max(rawPadding, MIN_Y_PADDING);
    const min = Math.max(0, Math.floor(chartStats.minDifficulty - padding));
    const max = Math.ceil(chartStats.maxDifficulty + padding);

    if (min === max) {
      return {
        min: Math.max(0, min - MIN_Y_PADDING),
        max: max + MIN_Y_PADDING,
      };
    }

    return {
      min,
      max,
    };
  }, [chartStats]);
  const chartWidth = useMemo(() => {
    if (chartPoints.length <= CHART_VISIBLE_LEVELS) {
      return '100%';
    }

    return `${chartPoints.length * CHART_PIXEL_PER_LEVEL}px`;
  }, [chartPoints.length]);
  const chartRevision = useMemo(() => {
    if (chartPoints.length === 0) {
      return '0';
    }

    const first = chartPoints[0].number;
    const last = chartPoints[chartPoints.length - 1].number;
    const sum = chartPoints.reduce((acc, p) => acc + p.difficulty, 0);
    return `${Lang}-${chartPoints.length}-${first}-${last}-${sum}`;
  }, [Lang, chartPoints]);
  const chartOptions = useMemo(() => {
    if (!chartRange) {
      return null;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Level number',
          },
          ticks: {
            maxTicksLimit: 12,
            autoSkip: true,
          },
          grid: {
            color: 'rgba(148, 163, 184, 0.25)',
          },
        },
        y: {
          min: chartRange.min,
          max: chartRange.max,
          title: {
            display: true,
            text: 'Difficulty',
          },
          grid: {
            color: 'rgba(148, 163, 184, 0.25)',
          },
        },
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false,
      },
    };
  }, [chartRange]);

  const mapsByNumber = useMemo(
    () => new Map<number, Level>(data.Maps.map((map) => [map.number, map])),
    [data.Maps]
  );

  const groups = useMemo(() => {
    return data.Settings.map((setting) => {
      const levelIndexes = [] as number[];
      const levelsByNumber = new Map<number, Level>();
      const missedLevels = [] as number[];

      for (let i = setting.start; i <= setting.end; i++) {
        levelIndexes.push(i);
        const level = mapsByNumber.get(i);
        if (level) {
          levelsByNumber.set(i, level);
        } else {
          missedLevels.push(i);
        }
      }

      return {
        setting: setting,
        levelIndexes: levelIndexes,
        levelsByNumber: levelsByNumber,
        missedLevels: missedLevels,
      };
    });
  }, [data.Settings, mapsByNumber]);

  useEffect(() => {
    if (scrollExecuted.current || data.Loading || groups.length === 0) return;

    const match = hash.match(/^#level-(\d+)$/);
    if (!match) return;

    const levelNum = parseInt(match[1], 10);
    const groupIndex = groups.findIndex(
      (g) => levelNum >= g.setting.start && levelNum <= g.setting.end
    );

    if (groupIndex !== -1) {
      scrollExecuted.current = true;
      setActiveTabIndex(groupIndex);
      setTimeout(() => {
        const tabs = document.querySelectorAll('.p-accordion-tab');
        if (tabs[groupIndex]) {
          (tabs[groupIndex] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [data.Loading, groups, hash]);

  const renderLevelGroup = (
    setting: PackSettings,
    levelIndexes: number[],
    levelsByNumber: Map<number, Level>,
    missedLevels: number[],
    isActive: boolean
  ) => {
    const size = setting.end - setting.start + 1;

    const header = (
      <div className="flex align-items-center gap-2 w-full flex-wrap">
        <span className="font-bold white-space-nowrap">
          {setting.start} - {setting.end} ({size})
        </span>
        <div className="ml-auto">
          <Button
            icon="pi pi-th-large"
            disabled={missedLevels.length === 0}
            onClick={(e) => onGenerate(e, missedLevels)}
            label={`Build missed`}
            badge={missedLevels.length.toString()}
            text
            size="small"
          />
        </div>
      </div>
    );

    const listTemplate = (_indexes: number[]) => {
      return (
        <div className="grid grid-nogutter justify-content-start gap-2">
          {levelIndexes.map((n) => {
            const level = levelsByNumber.get(n);
            const keyword = level ? level.keyword : '######';
            const rowKey = level ? `lvl-${level.id}-${level.number}` : `slot-${n}`;

            return (
              <div key={rowKey} className="flex flex-column">
                <div className="flex flex-row align-items-center gap-2 w-full flex-wrap">
                  <span className="font-bold white-space-nowrap">
                    {n} - {keyword}
                  </span>
                  <div className="ml-auto flex gap-1">
                    <Button
                      size="small"
                      icon="pi pi-pencil"
                      disabled={!level}
                      onClick={() => level && onEdit(level.id)}
                      text
                    />
                    <Button
                      size="small"
                      icon="pi pi-replay"
                      onClick={(e) => onGenerate(e, [n])}
                      text
                    />
                  </div>
                </div>
                <div className="overflow-auto">
                  <SVGCardRenderComponent
                    level={level}
                    width={setting.levelWidth}
                    height={setting.levelHeight}
                    placeholderText={level ? undefined : '+'}
                    onPlaceholderClick={level ? undefined : () => onCreate(n)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <AccordionTab key={setting.end} header={header}>
        {isActive && <DataView value={levelIndexes} listTemplate={listTemplate} />}
      </AccordionTab>
    );
  };

  if (data.Loading) {
    return <LoadingComponent />;
  }

  return (
    <div className="page-maps p-3">
      <ConfirmDialog />
      <div className="difficulty-chart mb-3">
        <div className="difficulty-chart__header">
          <div className="difficulty-chart__title">Difficulty ({Lang})</div>
          <div className="difficulty-chart__actions">
            <Button
              size="small"
              icon="pi pi-refresh"
              label="Recalculate"
              onClick={recalculateDifficulty}
              loading={recalculateLoading}
              disabled={recalculateLoading || reorderLoading}
            />
            <Button
              size="small"
              icon="pi pi-sort-numeric-down"
              label="Re-order"
              onClick={onReorderClick}
              loading={reorderLoading}
              disabled={reorderLoading || recalculateLoading || chartPoints.length === 0}
            />
          </div>
        </div>
        <div className="difficulty-chart__container">
          {chartPoints.length === 0 && (
            <div className="difficulty-chart__empty">No levels for selected language</div>
          )}
          {chartData && (
            <div className="difficulty-chart__scroll">
              <div className="difficulty-chart__chart-wrap" style={{ width: chartWidth }}>
                <Chart
                  key={chartRevision}
                  type="line"
                  data={chartData}
                  options={chartOptions ?? undefined}
                  className="difficulty-chart__chart"
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          )}
          {chartPoints.length > 0 && (
            <div className="difficulty-chart__meta">
              Levels: {chartPoints[0].number}-{chartPoints[chartPoints.length - 1].number} | Max
              difficulty:
              {chartStats?.maxDifficulty ?? 0}
            </div>
          )}
        </div>
      </div>
      <Accordion
        activeIndex={activeTabIndex ?? undefined}
        onTabChange={(event) => {
          const index = Array.isArray(event.index)
            ? (event.index[0] ?? null)
            : (event.index ?? null);
          setActiveTabIndex(index);
        }}
      >
        {groups.map((group, index) => {
          return renderLevelGroup(
            group.setting,
            group.levelIndexes,
            group.levelsByNumber,
            group.missedLevels,
            activeTabIndex === index
          );
        })}
      </Accordion>
    </div>
  );
};

export default MapsPage;
